import { useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useAppDispatch, useAppSelector } from '../../../helpers/hooks';
import { useWorkspaceSlice } from '../slice';
import { selectProject } from '../slice/selectors';
import {
  selectLanguageMap,
  selectLanguages,
} from '../../languages/slice/selectors';
import { ProjectRole } from '../../../helpers/types';

interface SettingsTabProps {
  projectId: string;
}

export default function SettingsTab({ projectId }: SettingsTabProps) {
  const dispatch = useAppDispatch();
  const { actions } = useWorkspaceSlice();
  const project = useAppSelector(selectProject);
  const languages = useAppSelector(selectLanguages);
  const languageMap = useAppSelector(selectLanguageMap);

  const [newLang, setNewLang] = useState('');
  const [memberEmail, setMemberEmail] = useState('');
  const [memberRole, setMemberRole] = useState<ProjectRole>(ProjectRole.EDITOR);

  if (!project) return null;

  const available = languages.filter(
    (l) =>
      l.code !== project.sourceLanguage &&
      !project.targetLanguages.includes(l.code),
  );

  const addLanguage = () => {
    if (newLang) {
      dispatch(actions.addLanguageRequest({ projectId, code: newLang }));
      setNewLang('');
    }
  };

  const addMember = () => {
    if (memberEmail) {
      dispatch(
        actions.addMemberRequest({
          projectId,
          email: memberEmail,
          role: memberRole,
        }),
      );
      setMemberEmail('');
    }
  };

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Target languages
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}
          >
            {project.targetLanguages.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No target languages yet.
              </Typography>
            )}
            {project.targetLanguages.map((code) => (
              <Chip
                key={code}
                label={`${languageMap[code] || code} (${code})`}
                onDelete={() =>
                  dispatch(actions.removeLanguageRequest({ projectId, code }))
                }
              />
            ))}
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={1} alignItems="center">
            <Select
              size="small"
              value={newLang}
              displayEmpty
              onChange={(e) => setNewLang(e.target.value)}
              sx={{ minWidth: 240 }}
            >
              <MenuItem value="" disabled>
                Select a language…
              </MenuItem>
              {available.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  {lang.name} ({lang.code})
                </MenuItem>
              ))}
            </Select>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addLanguage}
              disabled={!newLang}
            >
              Add
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Members
          </Typography>
          <List dense>
            {project.members.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No additional members.
              </Typography>
            )}
            {project.members.map((member) => (
              <ListItem
                key={member.user}
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={() =>
                      dispatch(
                        actions.removeMemberRequest({
                          projectId,
                          memberId: member.user,
                        }),
                      )
                    }
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemText primary={member.user} secondary={member.role} />
              </ListItem>
            ))}
          </List>
          <Divider sx={{ my: 2 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              size="small"
              placeholder="member@email.com"
              value={memberEmail}
              onChange={(e) => setMemberEmail(e.target.value)}
              sx={{ flexGrow: 1 }}
            />
            <Select
              size="small"
              value={memberRole}
              onChange={(e) => setMemberRole(e.target.value as ProjectRole)}
              sx={{ minWidth: 140 }}
            >
              {Object.values(ProjectRole).map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addMember}
              disabled={!memberEmail}
            >
              Add member
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
